import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/** Sessão de OPU de um serviço, ou null se ainda não existe. */
export const fetchOpuSession = (serviceId) =>
  cached(`opu-session:${serviceId}`, () => run(supabase
    .from('opu_sessions')
    .select('*')
    .eq('service_id', serviceId)
    .maybeSingle()));

/** Doadoras da sessão, na ordem em que foram lançadas. */
export const fetchOpuDonors = (sessionId) =>
  cached(`opu-donors:${sessionId}`, () => run(supabase
    .from('opu_donors')
    .select('*')
    .eq('opu_session_id', sessionId)
    .order('sort_order')));

/** Grava a sessão (uma por serviço) e devolve o id. */
export const upsertOpuSession = async (payload) => {
  const saved = await run(supabase
    .from('opu_sessions')
    .upsert(payload, { onConflict: 'service_id' })
    .select('id')
    .single());
  return saved.id;
};

/**
 * Grava a sessão e as doadoras como uma operação só.
 *
 * As doadoras chegam SEM `opu_session_id` de propósito: offline ainda não
 * existe id de sessão, e ele só é conhecido no momento em que a operação
 * roda de verdade. Amarrar os dois passos aqui é o que permite enfileirar
 * "salvei a OPU" em vez de dois fragmentos que podem subir pela metade.
 */
export const saveOpuSession = queueable('opu.save',
  async (sessionPayload, donorRows) => {
    const sessionId = await upsertOpuSession(sessionPayload);
    await replaceOpuDonors(sessionId, donorRows.map(r => ({ ...r, opu_session_id: sessionId })));
    return sessionId;
  },
  { pendingOf: (sessionPayload, _rows, appData) => appData && {
      kind: 'opu', id: sessionPayload.service_id, data: appData, label: 'Sessão de OPU',
    } });

/**
 * Troca as doadoras da sessão pelas informadas.
 *
 * É apaga-e-reinsere, não atualização linha a linha: as doadoras não têm
 * identidade estável do lado do app (a lista é reordenável e itens novos só
 * ganham id ao salvar). Manter esse par junto numa função é o que permite,
 * na fase da fila, tratar os dois passos como uma operação só.
 */
export const replaceOpuDonors = async (sessionId, rows) => {
  await run(supabase.from('opu_donors').delete().eq('opu_session_id', sessionId));
  if (rows.length > 0) {
    await run(supabase.from('opu_donors').insert(rows));
  }
};
